import React, { useEffect, useState, useCallback } from 'react';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Link from '@material-ui/core/Link';
import { styled } from '@material-ui/core/styles';
import dsiloDarkLogo from '../../images/dsilo-logo-dark.png';
import { deleteCookie, getCookie, setCookie } from './utils';
import { Request } from "../../modules/api/clientHelper";

// Constants
const USER_COOKIE_CONSENT_KEY = 'user_cookie_consent';
const COOKIE_EXPIRY_DAYS = 30;

const CustomButton = styled(Button)({
	backgroundColor: '#7900c0',
	border: 'none',
	borderRadius: '100px',
	color: '#fff',
	cursor: 'pointer',
	display: 'flex',
	gap: '48px',
	justifyContent: 'center',
	padding: '8px 32px 8px 32px',
	padding: '16px 48px',
	textTransform: 'none',
	'&:hover': {
		backgroundColor: '#7900c0',
		boxShadow: '0 8px 24px 0 #7900c0',
		textTransform: 'capitalize',
	},
});

const DialogActionCenter = styled(DialogActions)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '32px',
	marginBottom: '32px',
});

const CookiePolicy = styled(Box)({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'column',
	gap: '32px',
	justifyContent: 'center',
	margin: '24px',
});

const StyledLink = styled(Link)({
	color: '#7900c0',
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover': {
		color: '#7900c0',
		textDecoration: 'none',
	},
});

const StyledDailog = styled(Dialog)({
	'& .MuiDialog-paper': {
		borderRadius: '16px',
	},
});

export default function CookieConsent() {
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const BASE_URL = process.env.REACT_APP_API_BASE_URL;

	const getUserCookieConsent = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const response = await Request(BASE_URL + '/cc/gucc', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			return response;
		} catch (error) {
			console.error('Error fetching user cookie consent:', error);
			setError('Failed to fetch cookie consent status');
			return null;
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		const checkCookieConsent = async () => {
			try {
				const cookieConsent = await getUserCookieConsent();
				console.log("cookieConsent =====", cookieConsent);

				const cookieConsentLocal = getCookie(USER_COOKIE_CONSENT_KEY);

				// If user has already given consent locally, don't show dialog
				if (cookieConsentLocal) {
					return;
				}

				// If no consent from server and no local consent, show dialog
				if (!cookieConsent?.ucc) {
					toggleCookieConsent();
				}
			} catch (error) {
				console.error('Error in cookie consent check:', error);
				// On error, show consent dialog as fallback
				setOpen(false)
			}
		};

		checkCookieConsent();
	}, [getUserCookieConsent]);

	const handleCookieConsent = async () => {
		try {
			setIsLoading(true);
			setError(null);
			setOpen(false)

			// Set user cookie consent status on server
			const response = await Request(BASE_URL + '/cc/ucc', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			console.log(response, 'User cookie consent status');

			// Set local cookie to remember consent
			setCookie(USER_COOKIE_CONSENT_KEY, '1', COOKIE_EXPIRY_DAYS);
		} catch (error) {
			console.error('Error setting user cookie consent status:', error);
			setError('Failed to save cookie consent');
			setOpen(false)
		} finally {
			setIsLoading(false);
			setOpen(false)
		}
	};

	const toggleCookieConsent = useCallback(() => {
		setOpen(prev => !prev);
	}, []);

	const handleDecline = () => {
		// Set a local flag to remember user declined (optional)
		setCookie(USER_COOKIE_CONSENT_KEY, '0', COOKIE_EXPIRY_DAYS);
		setOpen(false)
	};

	return (
		<div>
			<StyledDailog open={open} aria-labelledby="responsive-dialog-title">
				<DialogContent>
					<DialogContentText>
						<CookiePolicy>
							<img width={140} loading="lazy" src={dsiloDarkLogo} alt="dSilo" />
							<h2>Welcome to dsilo</h2>
							<p>
								We use cookies to personalize content and to provide you with an improved user
								experience. By continuing to browse this site you consent to the use of cookies.
							</p>
							<p>
								Please visit our{' '}
								<StyledLink
									variant="body2"
									target='_blank'
									href='https://dsilo.ai/cookie-policy.php'
								// onClick={() => setShowCookiePolicy(true)}
								>
									Cookie policy
								</StyledLink>{' '}
								for further details
							</p>
						</CookiePolicy>
					</DialogContentText>
				</DialogContent>
				<DialogActionCenter>
					<CustomButton
						onClick={handleCookieConsent}
						disabled={isLoading}
					>
						{isLoading ? 'Processing...' : 'Accept'}
					</CustomButton>
					<CustomButton
						onClick={handleDecline}
						disabled={isLoading}
					>
						Decline
					</CustomButton>
				</DialogActionCenter>
				{error && (
					<Box style={{ textAlign: 'center', color: 'red', marginBottom: '16px' }}>
						{error}
					</Box>
				)}
			</StyledDailog>
			{/* <CookiePolicyTerms open={showCookiePolicy} handleClose={() => setShowCookiePolicy(false)} /> */}
		</div>
	);
}
